import { useCallback, useMemo } from 'react';
import { cloneDeep } from 'lodash';
import useCommon from 'hooks/useCommon';
import sessionStorageService from 'libs/sessionStorageService';

const PROFILE_FIRST_INTENDED_URL = 'PROFILE_FIRST_INTENDED_URL';

export interface IIncompleteRequiredForm {
  title: string;
  assigned_to_type: string;
  form_id: number;
  unit_id: number | null;
}

const useIncompleteForms = () => {
  const { common, setCommon } = useCommon();

  const forms: IIncompleteRequiredForm[] = useMemo(
    () => common.profile?.incomplete_required_forms ?? [],
    [common.profile?.incomplete_required_forms]
  );

  const isIncompleteRequiredForm = useCallback(
    (formId: number) => forms.some((f) => f.form_id === formId),
    [forms]
  );

  const removeForm = useCallback(
    (formId: number): IIncompleteRequiredForm[] => {
      if (!common.profile?.incomplete_required_forms) return [];

      const newCommon = cloneDeep(common);
      if (newCommon?.profile?.incomplete_required_forms) {
        newCommon.profile.incomplete_required_forms =
          newCommon.profile.incomplete_required_forms.filter((f) => f.form_id !== formId);
        setCommon(newCommon);
        return newCommon.profile.incomplete_required_forms;
      }
      return [];
    },
    [common, setCommon]
  );

  return { forms, isIncompleteRequiredForm, removeForm };
};

export const handleIncompleteFormRedirect = (
  forms: IIncompleteRequiredForm[] | undefined,
  currentPath: string
): string | null => {
  if (!Array.isArray(forms) || forms.length === 0) return null;

  const { form_id, unit_id } = forms[0];
  const params = new URLSearchParams();
  if (unit_id) params.set('unit_id', String(unit_id));
  params.set('tab', 'forms');

  const redirectPath = `/profile/forms/${form_id}?${params.toString()}`;

  if (currentPath !== redirectPath) {
    saveFirstIntendedUrl(currentPath);
    return redirectPath;
  }

  return null;
};

export const saveFirstIntendedUrl = (path: string) => {
  if (!sessionStorageService.has(PROFILE_FIRST_INTENDED_URL)) {
    sessionStorageService.set(PROFILE_FIRST_INTENDED_URL, path);
  }
};

export const consumeFirstIntendedUrl = (): string | null => {
  const path = sessionStorageService.get(PROFILE_FIRST_INTENDED_URL);
  if (path) sessionStorageService.remove(PROFILE_FIRST_INTENDED_URL);
  return path;
};

export default useIncompleteForms;
